import { useCallback, useEffect, useMemo } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import {
  ChartBarIcon,
  RocketIcon,
  Share2Icon,
  WrenchIcon,
  CircleCheckIcon,
  CircleIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { getRoles } from '@/util/util';

import { useAccount } from './useAccount';
import useMembers from './useMembers';
import { usePreferences } from './usePreferences';

// Main checklist enums
export enum StartEnum {
  ENVIRONMENT_SETUP = 'environment_setup',
  ADD_TEAM_MEMBERS = 'add_team_members',
}

export enum BuildEnum {
  IMPORT_CONTENT = 'import_content',
  CONNECT_BRAIN = 'connect_brain',
  CONFIGURE_GUIDELINES = 'configure_guidelines',
  CREATE_INSIGHT = 'create_insight',
  TEST_AGENT = 'test_agent',
  CREATE_VERSION = 'create_version',
}

export enum ConnectEnum {
  CONNECT_CHANNEL = 'connect_channel',
  UPDATE_RULE = 'update_rule',
}

export enum MeasureEnum {
  REVIEW_CONVERSATION = 'review_conversation',
  RECEIVE_MESSAGES = 'receive_messages',
  RESOLVE_CONVERSATION = 'resolve_conversation',
}

// Combined enum for all preferences
export const AccountUserPrefsEnum = {
  ...StartEnum,
  ...BuildEnum,
  ...ConnectEnum,
  ...MeasureEnum,
  COMPLETED: 'completed',
} as const;

type Keys = keyof typeof AccountUserPrefsEnum;
type ValuesTypes = (typeof AccountUserPrefsEnum)[Keys];

// Type definitions
export interface Step {
  label: string;
  completed: boolean;
  icon: React.ReactNode;
  url?: string;
  checklistStep?: ValuesTypes;
}

interface Tile {
  title: string;
  Icon: React.ComponentType<{ size?: number | string; color?: string; className?: string }>;
  steps: Step[];
}

type StepParams = {
  slug: string;
  brainId?: string;
  deskId?: string;
  integrationId?: string;
  ruleId?: string;
  collectionId?: string;
};

// Base step configuration type
type StepConfig = { label: string; urlBuilder?: (params: StepParams) => string };

// Base steps available to all users
const BASE_STEP_CONFIGS: Record<string, StepConfig> = {
  [AccountUserPrefsEnum.ENVIRONMENT_SETUP]: { label: 'home.steps.step1' },
  [AccountUserPrefsEnum.RECEIVE_MESSAGES]: { label: 'home.steps.receive_messages' },
};

// Builder-specific steps (AI Agent management)
const BUILDER_STEP_CONFIGS: Record<string, StepConfig> = {
  [AccountUserPrefsEnum.IMPORT_CONTENT]: {
    label: 'home.steps.import_collections',
    urlBuilder: ({ slug, collectionId }) =>
      collectionId ? `/${slug}/knowledge-bases/${collectionId}` : `/${slug}/knowledge-bases`,
  },
  [AccountUserPrefsEnum.CONNECT_BRAIN]: {
    label: 'home.steps.connect_collection',
    urlBuilder: ({ slug, brainId }) =>
      brainId ? `/${slug}/ai-agents/${brainId}/guidelines` : `/${slug}/ai-agents`,
  },
  [AccountUserPrefsEnum.CONFIGURE_GUIDELINES]: {
    label: 'home.steps.configure_guidelines',
    urlBuilder: ({ slug, brainId }) =>
      brainId ? `/${slug}/ai-agents/${brainId}/guidelines` : `/${slug}/ai-agents`,
  },
  [AccountUserPrefsEnum.CREATE_INSIGHT]: {
    label: 'home.steps.create_insight',
    urlBuilder: ({ slug, brainId }) =>
      brainId ? `/${slug}/ai-agents/${brainId}/insights` : `/${slug}/ai-agents`,
  },
  [AccountUserPrefsEnum.TEST_AGENT]: {
    label: 'home.steps.test_agent',
    urlBuilder: ({ slug, brainId }) =>
      brainId ? `/${slug}/ai-agents/${brainId}` : `/${slug}/ai-agents`,
  },
  [AccountUserPrefsEnum.CREATE_VERSION]: {
    label: 'home.steps.create_version',
    urlBuilder: ({ slug, brainId }) =>
      brainId ? `/${slug}/ai-agents/${brainId}` : `/${slug}/ai-agents`,
  },
};
// Chat Agent specific steps
const CHAT_AGENT_STEP_CONFIGS: Record<string, StepConfig> = {
  [AccountUserPrefsEnum.RESOLVE_CONVERSATION]: {
    label: 'home.steps.resolve_conversation',
    urlBuilder: ({ slug, deskId }) => `/${slug}/chats/${deskId}/conversations`,
  },
};

// Chat Manager specific steps
const CHAT_MANAGER_STEP_CONFIGS: Record<string, StepConfig> = {
  [AccountUserPrefsEnum.REVIEW_CONVERSATION]: {
    label: 'home.steps.review_conversation',
    urlBuilder: ({ slug }) => `/${slug}/analytics`,
  },
};

// Admin-specific steps
const ADMIN_STEP_CONFIGS: Record<string, StepConfig> = {
  [AccountUserPrefsEnum.ADD_TEAM_MEMBERS]: {
    label: 'home.steps.add_team_members',
    urlBuilder: ({ slug }) => `/${slug}/account/manage/member-access`,
  },
  [AccountUserPrefsEnum.CONNECT_CHANNEL]: {
    label: 'home.steps.connect_channel',
    urlBuilder: ({ slug, deskId, integrationId }) =>
      integrationId
        ? `/${slug}/environments/${deskId}/integrations/${integrationId}`
        : `/${slug}/environments/${deskId}/integrations`,
  },
  [AccountUserPrefsEnum.UPDATE_RULE]: {
    label: 'home.steps.configure_rules',
    urlBuilder: ({ slug, deskId, ruleId }) =>
      ruleId
        ? `/${slug}/environments/${deskId}/rules/${ruleId}`
        : `/${slug}/environments/${deskId}/rules`,
  },
};

// Helper function to get step configs based on user roles
const getUserStepConfigs = (
  role_ids: string[] | undefined,
  accountRole: string
): Record<string, StepConfig> => {
  if (!role_ids) {
    return {};
  }

  const { isBuilder, isChatManager, isAdminOrOwner, isChatAgent } = getRoles(role_ids, accountRole);
  let configs = { ...BASE_STEP_CONFIGS };

  if (isBuilder || isAdminOrOwner) {
    configs = { ...configs, ...BUILDER_STEP_CONFIGS };
  }

  if (isChatManager || isAdminOrOwner) {
    configs = { ...configs, ...CHAT_MANAGER_STEP_CONFIGS };
  }

  if (isChatAgent) {
    configs = { ...configs, ...CHAT_AGENT_STEP_CONFIGS };
  }

  if (isAdminOrOwner) {
    configs = { ...configs, ...ADMIN_STEP_CONFIGS };
  }
  return configs;
};

// Helper to create consistent step icons
const getStepIcon = (completed: boolean) =>
  completed ? (
    <CircleCheckIcon className="text-successful" size={16} />
  ) : (
    <CircleIcon size={16} className="text-foreground-muted" />
  );

// Helper to create a step from configuration
const createStepFromConfig = (
  key: ValuesTypes,
  prefs: Record<string, boolean>,
  params: StepParams,
  configs: Record<string, StepConfig>
): Step | null => {
  const config = configs[key];
  if (!config) {
    return null;
  }

  return {
    label: config.label,
    completed: !!prefs[key],
    icon: getStepIcon(!!prefs[key]),
    url: config.urlBuilder?.(params),
    checklistStep: key,
  };
};

// Main hook
const useHomeCheckList = () => {
  const { accountUserPrefs, updateAccountUserPreferences } = usePreferences();
  const { t } = useTranslation();
  const { accountStatus, accountRole } = useAccount();

  const { member } = useMembers();
  const role_ids = member?.role_ids;

  const checklist = useMemo(
    () => (accountUserPrefs?.checklist ?? {}) as Record<string, boolean>,
    [accountUserPrefs?.checklist]
  );

  /**
   * Marks a checklist item as complete
   * @param key - The checklist item key
   * @param suppressToast - If true, suppresses the success toast notification
   */
  const markAsComplete = useCallback(
    (key: ValuesTypes, suppressToast = false) => {
      if (checklist && !checklist[key]) {
        updateAccountUserPreferences(
          { checklist: { ...checklist, [key]: true } },
          {
            onSuccess: () => {
              trackEvent(EventName.ChecklistStepCompleted, { checklist_step: key });
              const formattedKey = key?.split('_')?.join(' ');
              if (key !== AccountUserPrefsEnum.COMPLETED && !suppressToast) {
                toast.success(t('home.checklist.success', { 0: formattedKey }));
              }
            },
          }
        );
      }
    },
    [checklist, t, updateAccountUserPreferences]
  );

  // Auto-complete checklist when all role-appropriate items are done
  useEffect(() => {
    if (!checklist[AccountUserPrefsEnum.COMPLETED] && accountStatus === 'success' && role_ids) {
      // Get only the items that this user role can complete
      const userConfigs = getUserStepConfigs(role_ids, accountRole);
      const relevantItems = Object.keys(userConfigs);

      // Check if all relevant items are completed
      const allCompleted =
        relevantItems.length > 0 && relevantItems.every((item) => checklist[item]);

      if (allCompleted && !isEmpty(checklist)) {
        markAsComplete(AccountUserPrefsEnum.COMPLETED);
      }
    }
  }, [accountStatus, checklist, markAsComplete, role_ids, accountRole]);

  // Generate steps based on preferences and parameters
  const getSteps = useCallback(
    (
      prefs: Record<string, boolean>,
      slug: string,
      brainId: string,
      deskId: string,
      integrationId?: string,
      ruleId?: string,
      collectionId?: string
    ) => {
      if (!prefs || !role_ids) {
        return [];
      }

      const params: StepParams = { slug, brainId, deskId, integrationId, ruleId, collectionId };
      const userConfigs = getUserStepConfigs(role_ids, accountRole);

      // Create all tiles with their steps
      const tiles: Tile[] = [
        {
          title: 'home.steps.title1',
          Icon: RocketIcon,
          steps: [
            createStepFromConfig(
              AccountUserPrefsEnum.ENVIRONMENT_SETUP,
              prefs,
              params,
              userConfigs
            ),
            createStepFromConfig(AccountUserPrefsEnum.ADD_TEAM_MEMBERS, prefs, params, userConfigs),
          ].filter(Boolean) as Step[],
        },
        {
          title: 'home.steps.title2',
          Icon: WrenchIcon,
          steps: [
            createStepFromConfig(AccountUserPrefsEnum.IMPORT_CONTENT, prefs, params, userConfigs),
            createStepFromConfig(AccountUserPrefsEnum.CONNECT_BRAIN, prefs, params, userConfigs),
            createStepFromConfig(
              AccountUserPrefsEnum.CONFIGURE_GUIDELINES,
              prefs,
              params,
              userConfigs
            ),
            createStepFromConfig(AccountUserPrefsEnum.CREATE_INSIGHT, prefs, params, userConfigs),
            createStepFromConfig(AccountUserPrefsEnum.TEST_AGENT, prefs, params, userConfigs),
            createStepFromConfig(AccountUserPrefsEnum.CREATE_VERSION, prefs, params, userConfigs),
          ].filter(Boolean) as Step[],
        },
        {
          title: 'home.steps.title3',
          Icon: Share2Icon,
          steps: [
            createStepFromConfig(AccountUserPrefsEnum.CONNECT_CHANNEL, prefs, params, userConfigs),
            createStepFromConfig(AccountUserPrefsEnum.UPDATE_RULE, prefs, params, userConfigs),
          ].filter(Boolean) as Step[],
        },
        {
          title: 'home.steps.title4',
          Icon: ChartBarIcon,
          steps: [
            createStepFromConfig(
              AccountUserPrefsEnum.REVIEW_CONVERSATION,
              prefs,
              params,
              userConfigs
            ),
            createStepFromConfig(
              AccountUserPrefsEnum.RESOLVE_CONVERSATION,
              prefs,
              params,
              userConfigs
            ),
            createStepFromConfig(AccountUserPrefsEnum.RECEIVE_MESSAGES, prefs, params, userConfigs),
          ].filter(Boolean) as Step[],
        },
      ];

      // Filter out empty tiles (tiles with no steps after role filtering)
      return tiles.filter((tile) => tile.steps.length > 0);
    },
    [role_ids, accountRole]
  );

  return { accountUserChecklist: checklist, markAsComplete, getSteps };
};

export default useHomeCheckList;
